import React, { useState } from "react";
import Nav from "../components/sections/Nav";
import Footer from "../components/sections/Footer";
import Seo from "../components/Seo";
import RevealOnScroll from "../components/RevealOnScroll";
import { BOTANICAL_SRC } from "../components/BotanicalOverlay";

const API = process.env.REACT_APP_BACKEND_URL;

// Alice palette — muted teal, berry, dusty rose-purple. No gold, no terracotta.
const CREAM = "#FBF6F1";
const CREAM_SOFT = "#F5EDE3";
const CHARCOAL = "#2C2825";
const BERRY = "#7A2D50";
const TEAL = "#5C7A78";
const ROSE_PURPLE = "#A88CA0";
const BOOK_BTN_COLOR = "#7A2D50";

const READINGS = [
    {
        testid: "reading-clarity-pull",
        title: "The Clarity Pull",
        price: "$27",
        priceLabel: "The Clarity Pull — $27",
        tagline: "A focused reading for a single question or area of life.",
        body:
            "This reading uses a small, intentional card pull designed to bring clarity to one specific question you're sitting with. It is not about prediction — it is about reflection. What is present right now? What might be worth paying attention to?",
        exploresLabel: "The cards in this reading explore:",
        explores: [
            "What is present in this situation right now",
            "What may be working against clarity or movement",
            "What is being called forward",
        ],
        bestFor: "Best for a clear, defined question or a moment of decision.",
        receive:
            "What you receive: a written interpretation delivered by email within 5 business days.",
        // Solid teal card — white text, no interior pattern.
        surface: TEAL,
        borderColor: "rgba(255,255,255,0.28)",
    },
    {
        testid: "reading-compass-rose",
        title: "The Compass Rose",
        price: "$77",
        priceLabel: "The Compass Rose — $77",
        tagline: "A multi-card spread for those ready to look at the fuller picture.",
        body:
            "The Compass Rose explores where you are across several dimensions — not just what is happening, but what surrounds it, what supports it, and what it might be asking of you. It is designed for moments when a single card isn't enough to hold the complexity of what you're navigating.",
        exploresLabel: "The cards in this reading explore:",
        explores: [
            "Where you are right now",
            "What is surrounding and influencing this situation",
            "What is challenging you or creating resistance",
            "What is supporting you, even if quietly",
            "What values or lessons might be asking for your attention",
        ],
        bestFor: "Best for layered situations or when you want more than a single reflection.",
        receive:
            "What you receive: a detailed written interpretation delivered by email within 5 business days.",
        // Solid berry card.
        surface: BERRY,
        borderColor: "rgba(255,255,255,0.28)",
    },
    {
        testid: "reading-custom-spread",
        title: "Custom Spread",
        price: "$97",
        priceLabel: "Custom Spread + 20-minute call — $97",
        tagline: "A reading designed entirely around your question — with a conversation first.",
        body:
            "If neither of the above spreads quite fits what you're carrying, this is for you. Before the reading begins, Austine will schedule a brief 20-minute phone call to hear your situation directly and design a spread specifically around it. Nothing is templated. The positions, the depth, the focus — all of it is built around you.",
        exploresLabel: "This reading includes:",
        explores: [
            "A 20-minute intake call with Austine before the reading begins",
            "A spread designed specifically for your question and situation",
            "A detailed written interpretation built around the custom layout",
        ],
        bestFor: "Best for complex or layered situations that don't fit a standard format.",
        receive:
            "What you receive: a custom-designed reading delivered by email within 5 business days of your call.",
        // Solid dusty rose-purple card.
        surface: ROSE_PURPLE,
        borderColor: "rgba(255,255,255,0.32)",
    },
];

function ReadingBox({ r }) {
    // Text on a saturated surface — near-white cream for the body, and a slightly
    // brighter white for the title/price to give a jewel-like hierarchy.
    const heading = "#FFFFFF";
    const bodyText = "rgba(253,249,242,0.94)";
    const softText = "rgba(253,249,242,0.86)";

    return (
        <article
            data-testid={r.testid}
            className="relative flex flex-col p-8 lg:p-10 transition-transform duration-500 hover:-translate-y-1"
            style={{
                // Solid, opaque accent color. No patterns, no gradients, no overlays.
                backgroundColor: r.surface,
                border: `1px solid ${r.borderColor}`,
                color: heading,
                boxShadow: "0 12px 32px rgba(44,40,37,0.08)",
            }}
        >
            {/* Subtle corner ornaments — same tint as the text so the card stays jewel-like */}
            <span aria-hidden className="absolute top-5 left-5" style={{ color: softText, fontFamily: "'Cormorant Garamond', serif", fontSize: 16, opacity: 0.85 }}>✦</span>
            <span aria-hidden className="absolute bottom-5 right-5" style={{ color: softText, fontFamily: "'Cormorant Garamond', serif", fontSize: 16, opacity: 0.85 }}>✦</span>

            <span
                className="text-[10px] tracking-[0.34em] uppercase mb-4"
                style={{ color: softText, fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}
            >
                Reading
            </span>
            <h3
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontSize: 30,
                    lineHeight: 1.15,
                    color: heading,
                    fontWeight: 500,
                }}
            >
                {r.title}
            </h3>
            <p
                data-testid={`${r.testid}-price`}
                className="mt-3"
                style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13,
                    letterSpacing: "0.32em",
                    textTransform: "uppercase",
                    color: heading,
                    fontWeight: 600,
                }}
            >
                {r.price}
            </p>
            <div aria-hidden style={{ marginTop: 18, marginBottom: 20, height: 1, width: 52, background: "rgba(255,255,255,0.55)" }} />

            <p
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: 18,
                    lineHeight: 1.5,
                    color: bodyText,
                    marginBottom: 16,
                }}
            >
                {r.tagline}
            </p>
            <p
                style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 15,
                    lineHeight: 1.8,
                    color: bodyText,
                }}
            >
                {r.body}
            </p>

            <p
                className="mt-6"
                style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    letterSpacing: "0.28em",
                    textTransform: "uppercase",
                    color: heading,
                    fontWeight: 600,
                }}
            >
                {r.exploresLabel}
            </p>
            <ul className="mt-3 space-y-2" style={{ paddingLeft: 0, listStyle: "none" }}>
                {r.explores.map((line) => (
                    <li
                        key={line}
                        className="flex gap-3"
                        style={{
                            fontFamily: "'DM Sans', sans-serif",
                            fontSize: 14.5,
                            lineHeight: 1.7,
                            color: bodyText,
                        }}
                    >
                        <span aria-hidden style={{ color: softText, marginTop: 2 }}>✦</span>
                        <span>{line}</span>
                    </li>
                ))}
            </ul>

            <p
                className="mt-6"
                style={{
                    fontFamily: "'Cormorant Garamond', serif",
                    fontStyle: "italic",
                    fontSize: 16,
                    lineHeight: 1.6,
                    color: bodyText,
                }}
            >
                {r.bestFor}
            </p>

            <p
                className="mt-6 pt-5"
                style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 13.5,
                    lineHeight: 1.75,
                    color: softText,
                    borderTop: "1px solid rgba(255,255,255,0.28)",
                }}
            >
                {r.receive}
            </p>
        </article>
    );
}

const initialForm = {
    name: "",
    email: "",
    reading_type: "",
    question: "",
    desired_outcome: "",
    challenges: "",
    goal: "",
    dob: "",
    newsletter_clinical: false,
    newsletter_readings: false,
    ack_not_therapy: false,
    ack_not_prediction: false,
    ack_payment_required: false,
};

const fieldStyle = {
    background: "#FFFFFF",
    border: `1px solid ${ROSE_PURPLE}`,
    color: CHARCOAL,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 15,
    lineHeight: 1.7,
    padding: "12px 14px",
    width: "100%",
    outline: "none",
};

const labelStyle = {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 11,
    letterSpacing: "0.3em",
    textTransform: "uppercase",
    color: BERRY,
    fontWeight: 600,
    marginBottom: 6,
    display: "block",
};

const helperStyle = {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    color: "rgba(44,40,37,0.62)",
    marginTop: 6,
    fontStyle: "italic",
};

async function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function BookAReadingPage() {
    const [form, setForm] = useState(initialForm);
    const [photo, setPhoto] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);

    const set = (k) => (e) => {
        const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
        setForm((f) => ({ ...f, [k]: v }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            // Only attach photos ≤ 4 MB so the request stays well under proxy limits.
            let photo_data_url = null;
            if (photo && photo.size <= 4 * 1024 * 1024) {
                photo_data_url = await fileToDataUrl(photo);
            }
            const res = await fetch(`${API}/api/reading-request`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ ...form, photo_data_url }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({ detail: "Something went wrong." }));
                throw new Error(err.detail || "Could not send your request.");
            }
            setSubmitted(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (err) {
            setError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main data-testid="book-a-reading-page" style={{ background: CREAM, color: CHARCOAL }}>
            <Seo
                title="Book a Reading | Alchemy Practice"
                description="Book a tarot reading with Austine Roney. Three thoughtful readings — Clarity Pull, Compass Rose, or Custom Spread — delivered by email within 5 business days."
            />
            <Nav />
            <RevealOnScroll>
                {/* HERO */}
                <section
                    data-testid="book-a-reading-hero"
                    className="relative overflow-hidden pt-40 pb-16 lg:pt-48 lg:pb-20"
                    style={{ background: CREAM }}
                >
                    <div
                        aria-hidden
                        className="absolute inset-y-0 right-0 hidden lg:block pointer-events-none"
                        style={{
                            width: "32%",
                            backgroundImage: `url(${BOTANICAL_SRC})`,
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "right center",
                            backgroundSize: "contain",
                            opacity: 0.08,
                            mixBlendMode: "multiply",
                            transform: "scaleX(-1)",
                            WebkitMaskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,1) 100%)",
                            maskImage: "linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.55) 55%, rgba(0,0,0,1) 100%)",
                        }}
                    />
                    <div className="relative mx-auto max-w-[900px] px-6 lg:px-12 text-center" data-reveal>
                        <span
                            className="text-[11px] tracking-[0.34em] uppercase font-medium"
                            style={{ color: TEAL, fontFamily: "'DM Sans', sans-serif" }}
                        >
                            Reflection Deck
                        </span>
                        <h1
                            data-testid="book-a-reading-heading"
                            className="mt-6 leading-[1.02]"
                            style={{
                                fontFamily: "'Cormorant Garamond', serif",
                                fontSize: "clamp(46px, 6vw, 80px)",
                                color: CHARCOAL,
                                fontWeight: 500,
                            }}
                        >
                            Book a{" "}
                            <em style={{ fontStyle: "italic", color: BERRY, fontWeight: 500 }}>Reading</em>
                        </h1>
                        <div className="mx-auto mt-8 flex items-center justify-center gap-3" aria-hidden>
                            <span style={{ height: 1, width: 44, background: TEAL, opacity: 0.6 }} />
                            <span style={{ color: ROSE_PURPLE, fontSize: 14 }}>✦</span>
                            <span style={{ height: 1, width: 44, background: TEAL, opacity: 0.6 }} />
                        </div>
                        <p
                            data-testid="book-a-reading-intro"
                            className="mt-8 max-w-2xl mx-auto"
                            style={{
                                fontFamily: "'DM Sans', sans-serif",
                                fontSize: 17,
                                lineHeight: 1.9,
                                color: "rgba(44,40,37,0.86)",
                            }}
                        >
                            Each reading is completed by email within 5 business days. You will receive
                            a payment link when your reading is ready — your report is delivered once
                            payment is received.
                        </p>
                    </div>
                </section>

                {submitted ? (
                    <section
                        data-testid="book-a-reading-confirmation"
                        className="py-24 lg:py-32"
                        style={{ background: CREAM_SOFT }}
                    >
                        <div className="mx-auto max-w-[720px] px-6 lg:px-12 text-center" data-reveal>
                            <span aria-hidden style={{ color: BERRY, fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}>✦</span>
                            <h2
                                className="mt-4"
                                style={{
                                    fontFamily: "'Cormorant Garamond', serif",
                                    fontSize: "clamp(30px, 3.6vw, 42px)",
                                    color: TEAL,
                                    fontWeight: 500,
                                    lineHeight: 1.2,
                                }}
                            >
                                Thank you — your request has been{" "}
                                <em style={{ fontStyle: "italic", color: BERRY }}>received.</em>
                            </h2>
                            <p
                                className="mt-8 mx-auto"
                                style={{
                                    fontFamily: "'DM Sans', sans-serif",
                                    fontSize: 17,
                                    lineHeight: 1.9,
                                    color: "rgba(44,40,37,0.86)",
                                    maxWidth: 560,
                                }}
                            >
                                Austine will complete your reading within 5 business days and will be in
                                touch by email. If you selected the Custom Spread, she will reach out to
                                schedule your 20-minute call before the reading begins.
                            </p>
                        </div>
                    </section>
                ) : (
                    <>
                        {/* THREE READINGS */}
                        <section
                            data-testid="book-a-reading-cards-section"
                            className="relative pb-16 lg:pb-24 pt-4 overflow-hidden"
                            style={{ background: CREAM }}
                        >
                            {/* Section-level botanical watermark — sits behind the whole
                                three-card group, not inside any individual card. Very low
                                opacity so it reads as an atmospheric backdrop. */}
                            <div
                                aria-hidden
                                className="absolute inset-0 pointer-events-none"
                                data-testid="book-a-reading-cards-watermark"
                                style={{
                                    backgroundImage: `url(${BOTANICAL_SRC})`,
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "center",
                                    backgroundSize: "min(1400px, 92%)",
                                    opacity: 0.08,
                                    mixBlendMode: "multiply",
                                }}
                            />
                            <div className="relative mx-auto max-w-[1180px] px-6 lg:px-12">
                                <div className="grid md:grid-cols-3 gap-6 lg:gap-8" data-testid="book-a-reading-cards-grid">
                                    {READINGS.map((r) => <ReadingBox key={r.testid} r={r} />)}
                                </div>
                            </div>
                        </section>

                        {/* FORM */}
                        <section
                            data-testid="book-a-reading-form-section"
                            className="pb-24 lg:pb-32 pt-12"
                            style={{ background: CREAM_SOFT }}
                        >
                            <div className="mx-auto max-w-[820px] px-6 lg:px-12">
                                <div className="text-center mb-12" data-reveal>
                                    <span style={{ ...labelStyle, marginBottom: 12, color: TEAL, display: "inline-block" }}>Reading Request</span>
                                    <h2
                                        style={{
                                            fontFamily: "'Cormorant Garamond', serif",
                                            fontSize: "clamp(32px, 4vw, 46px)",
                                            color: CHARCOAL,
                                            fontWeight: 500,
                                            lineHeight: 1.15,
                                            marginTop: 8,
                                        }}
                                    >
                                        Submit Your{" "}
                                        <em style={{ fontStyle: "italic", color: BERRY }}>Reading Request</em>
                                    </h2>
                                </div>

                                <form onSubmit={submit} data-testid="book-a-reading-form" className="space-y-8">
                                    <div className="grid md:grid-cols-2 gap-6">
                                        <label className="block">
                                            <span style={labelStyle}>Full Name<span aria-hidden style={{ color: BERRY }}> *</span></span>
                                            <input type="text" required value={form.name} onChange={set("name")} data-testid="form-name" style={fieldStyle} />
                                        </label>
                                        <label className="block">
                                            <span style={labelStyle}>Email Address<span aria-hidden style={{ color: BERRY }}> *</span></span>
                                            <input type="email" required value={form.email} onChange={set("email")} data-testid="form-email" style={fieldStyle} />
                                        </label>
                                    </div>

                                    <label className="block">
                                        <span style={labelStyle}>Which reading are you requesting?<span aria-hidden style={{ color: BERRY }}> *</span></span>
                                        <select required value={form.reading_type} onChange={set("reading_type")} data-testid="form-reading-type" style={fieldStyle}>
                                            <option value="">Choose a reading…</option>
                                            {READINGS.map((r) => (
                                                <option key={r.testid} value={r.priceLabel}>{r.priceLabel}</option>
                                            ))}
                                        </select>
                                    </label>

                                    <label className="block">
                                        <span style={labelStyle}>Share your question and a little context about the situation.<span aria-hidden style={{ color: BERRY }}> *</span></span>
                                        <textarea rows={5} required value={form.question} onChange={set("question")} data-testid="form-question" style={fieldStyle} />
                                    </label>

                                    <label className="block">
                                        <span style={labelStyle}>Desired outcome — what are you hoping to walk away with?<span aria-hidden style={{ color: BERRY }}> *</span></span>
                                        <textarea rows={4} required value={form.desired_outcome} onChange={set("desired_outcome")} data-testid="form-desired-outcome" style={fieldStyle} />
                                    </label>

                                    <label className="block">
                                        <span style={labelStyle}>Current challenges (optional)</span>
                                        <textarea rows={3} value={form.challenges} onChange={set("challenges")} data-testid="form-challenges" style={fieldStyle} />
                                    </label>

                                    <label className="block">
                                        <span style={labelStyle}>What do you most want to get out of this reading?<span aria-hidden style={{ color: BERRY }}> *</span></span>
                                        <textarea rows={3} required value={form.goal} onChange={set("goal")} data-testid="form-goal" style={fieldStyle} />
                                    </label>

                                    <div className="grid md:grid-cols-2 gap-6">
                                        <label className="block">
                                            <span style={labelStyle}>Date of Birth</span>
                                            <input type="text" value={form.dob} onChange={set("dob")} data-testid="form-dob" placeholder="MM / DD / YYYY" style={fieldStyle} />
                                            <span style={helperStyle}>Optional. I sometimes use your Personal Year number to add depth to the reading.</span>
                                        </label>
                                        <label className="block">
                                            <span style={labelStyle}>Recent Photo</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                                                data-testid="form-photo"
                                                style={{ ...fieldStyle, padding: "10px 14px" }}
                                            />
                                            <span style={helperStyle}>Optional. Some readers use a recent photo to help attune to your energy before the reading begins.</span>
                                        </label>
                                    </div>

                                    <fieldset className="pt-2">
                                        <legend style={{ ...labelStyle, marginBottom: 12, position: "static" }}>Newsletter opt-ins</legend>
                                        <label className="flex items-start gap-3 mb-3 cursor-pointer">
                                            <input type="checkbox" checked={form.newsletter_clinical} onChange={set("newsletter_clinical")} data-testid="form-nl-clinical" style={{ accentColor: BERRY, marginTop: 4 }} />
                                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: CHARCOAL }}>
                                                I&rsquo;d like to receive updates about the clinical practice.
                                            </span>
                                        </label>
                                        <label className="flex items-start gap-3 cursor-pointer">
                                            <input type="checkbox" checked={form.newsletter_readings} onChange={set("newsletter_readings")} data-testid="form-nl-readings" style={{ accentColor: BERRY, marginTop: 4 }} />
                                            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: CHARCOAL }}>
                                                I&rsquo;d like to receive updates about readings and spiritual offerings.
                                            </span>
                                        </label>
                                    </fieldset>

                                    <fieldset
                                        className="pt-6 mt-6"
                                        style={{ borderTop: `1px solid ${ROSE_PURPLE}` }}
                                        data-testid="form-consent-section"
                                    >
                                        <legend
                                            style={{
                                                fontFamily: "'Cormorant Garamond', serif",
                                                fontStyle: "italic",
                                                fontSize: 20,
                                                color: TEAL,
                                                paddingRight: 12,
                                            }}
                                        >
                                            Before you submit, please acknowledge the following:
                                        </legend>
                                        {[
                                            {
                                                key: "ack_not_therapy",
                                                testid: "form-ack-not-therapy",
                                                text: "I understand this reading is not therapy and does not replace mental health treatment.",
                                            },
                                            {
                                                key: "ack_not_prediction",
                                                testid: "form-ack-not-prediction",
                                                text: "I understand this reading is not a prediction about the future.",
                                            },
                                            {
                                                key: "ack_payment_required",
                                                testid: "form-ack-payment-required",
                                                text: "I understand my reading will not be delivered until payment is received, and readings are non-refundable once delivered.",
                                            },
                                        ].map((c) => (
                                            <label key={c.key} className="flex items-start gap-3 mt-4 cursor-pointer">
                                                <input
                                                    type="checkbox"
                                                    required
                                                    checked={form[c.key]}
                                                    onChange={set(c.key)}
                                                    data-testid={c.testid}
                                                    style={{ accentColor: BERRY, marginTop: 4, width: 18, height: 18 }}
                                                />
                                                <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 15, color: CHARCOAL, lineHeight: 1.65 }}>
                                                    {c.text}
                                                </span>
                                            </label>
                                        ))}
                                    </fieldset>

                                    {error && (
                                        <p data-testid="form-error" style={{ color: "#B94A3A", fontFamily: "'DM Sans', sans-serif", fontSize: 14 }}>
                                            {error}
                                        </p>
                                    )}

                                    <div className="pt-4">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            data-testid="form-submit"
                                            className="inline-flex items-center justify-center gap-2 transition-opacity duration-300 hover:opacity-90 disabled:opacity-60"
                                            style={{
                                                background: BOOK_BTN_COLOR,
                                                color: "#FAFAF8",
                                                padding: "18px 40px",
                                                fontFamily: "'DM Sans', sans-serif",
                                                fontSize: 13,
                                                letterSpacing: "0.32em",
                                                textTransform: "uppercase",
                                                fontWeight: 500,
                                                border: "none",
                                                cursor: submitting ? "not-allowed" : "pointer",
                                            }}
                                        >
                                            {submitting ? "Sending…" : "Send My Reading Request"}
                                            {!submitting && <span aria-hidden>→</span>}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </section>
                    </>
                )}
                <Footer />
            </RevealOnScroll>
        </main>
    );
}
